import SearchIcon from "@mui/icons-material/Search"
import { Button } from "@mui/material"
import { styled } from "@mui/material/styles"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useState } from "react"
import AsyncSelect from "react-select/async"

import { apiClient } from "@/api"
import withAlerter from "../common/withAlerter"
import FormFieldErrorFeedback from "./FormFieldErrorFeedback"

const StyledDropdown = styled("div")(({ theme }) => ({
  position: "relative",
  minWidth: "10em",
  paddingRight: "1em !important",
  "&.form-control": {
    padding: "0.3rem 1rem",
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: "999px",
    "&.is-invalid": {
      borderColor: theme.palette.error.main,
    },
    "&.disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      cursor: "not-allowed",
    },
    "&.has-value": {
      backgroundColor: theme.palette.grey[50],
      borderColor: theme.palette.primary.main,
    },
  },
}))

const StyledButton = styled(Button)(({ theme, hasvalue }) => ({
  background: "none",
  width: "100%",
  padding: 0,
  justifyContent: "flex-start",
  color: hasvalue === "true" ? theme.palette.text.primary : theme.palette.text.secondary,
  height: "2.5rem",
  fontWeight: hasvalue === "true" ? "500" : "400",
  "&:hover": {
    background: "none",
  },
  "&.Mui-disabled": {
    color: theme.palette.text.disabled,
  },
}))

const Menu = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  marginTop: theme.spacing(1),
  position: "absolute",
  zIndex: theme.zIndex.modal,
  width: "100%",
}))

const Blanket = styled("div")(({ theme }) => ({
  bottom: 0,
  left: 0,
  top: 0,
  right: 0,
  position: "fixed",
  zIndex: theme.zIndex.modal - 1,
  backgroundColor: "transparent",
}))

const selectStyles = {
  control: () => ({
    display: "flex",
    width: "100%",
  }),
  menu: () => ({
    boxShadow: "none",
    marginTop: 0,
    marginBottom: 0,
  }),
  menuList: base => ({
    ...base,
    padding: 0,
  }),
}

const DropdownIndicator = () => <SearchIcon sx={{ color: "action.active", mr: 1 }} />

const AsyncAutocompleteField = ({
  alerter,
  className,
  source,
  dynamicSource,
  placeholder,
  fieldName,
  selected,
  validationErrors,
  disabled,
  onSelect,
  formIdToAutoSubmit,
  hideError,
  isMulti,
  clearAfterSelect = false,
  externalParams,
  defaultOptions = false,
  queryParam = "q",
  omitEmptyQuery = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState(selected)

  useEffect(() => {
    setValue(selected)
  }, [selected])

  const promiseOptions = useCallback(
    async query => {
      if (!query && !defaultOptions) {
        return []
      }

      try {
        if (typeof dynamicSource === "function") {
          return await dynamicSource(query)
        }

        if (source) {
          const params = {
            ...externalParams,
          }

          // Only add query parameter if there's a query or if we shouldn't omit empty queries
          if (query || !omitEmptyQuery) {
            params[queryParam] = query || ""
          }

          const {
            data: { items },
          } = await apiClient.get(source, { params })
          return items
        }

        throw new Error("Autocomplete source must be defined")
      } catch (error) {
        alerter.error("Failed to load options")
        return []
      }
    },
    [externalParams, alerter, source, dynamicSource, defaultOptions, queryParam, omitEmptyQuery]
  )

  const toggleOpen = () => setIsOpen(!isOpen)

  const handleSelect = newValue => {
    if (Array.isArray(value) && newValue === null) {
      newValue = []
    }

    toggleOpen()
    setValue(newValue)

    if (typeof onSelect === "function") {
      onSelect(newValue)
    }

    if (clearAfterSelect) {
      setValue(null)
    }

    if (formIdToAutoSubmit) {
      const form = document.forms[formIdToAutoSubmit]
      const input = document.querySelector(`[name=${fieldName}]`)

      if (form && input) {
        input.value = newValue ? newValue.value : ""
        form.submit()
      }
    }
  }

  return (
    <>
      <StyledDropdown
        className={`form-control ${validationErrors ? "is-invalid" : ""} ${disabled ? "disabled" : ""} ${
          value ? "has-value" : ""
        } ${className || ""}`}
      >
        <StyledButton
          onClick={() => !disabled && toggleOpen()}
          disabled={disabled}
          disableRipple
          variant="text"
          hasvalue={(!!value).toString()}
        >
          {isMulti
            ? (value && value.length > 0 && value.map(({ title }) => title).join(", ")) || placeholder
            : (value && value.title) || placeholder}
        </StyledButton>

        {fieldName &&
          isMulti &&
          value?.map(({ value }, idx) => (
            <input key={idx} type="checkbox" checked hidden readOnly name={fieldName} value={value} />
          ))}

        {fieldName && !isMulti && <input type="hidden" name={fieldName} value={(value && value.value) || ""} />}

        {isOpen && (
          <>
            <Menu>
              <AsyncSelect
                isMulti={isMulti}
                autoFocus
                components={{ DropdownIndicator, IndicatorSeparator: null }}
                hideSelectedOptions={false}
                isClearable
                menuIsOpen
                onChange={handleSelect}
                styles={selectStyles}
                tabSelectsValue={false}
                name={formIdToAutoSubmit ? null : fieldName}
                cacheOptions
                noOptionsMessage={e => (e.inputValue ? "No options" : null)}
                defaultValue={value}
                defaultOptions
                loadOptions={promiseOptions}
                getOptionLabel={option => option.title}
              />
            </Menu>
            <Blanket onClick={toggleOpen} />
          </>
        )}
      </StyledDropdown>

      {!hideError && <FormFieldErrorFeedback message={validationErrors} />}
    </>
  )
}

AsyncAutocompleteField.propTypes = {
  source: PropTypes.string,
  dynamicSource: PropTypes.func,
  placeholder: PropTypes.string,
  fieldName: PropTypes.string,
  selected: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
  }),
  externalParams: PropTypes.object,
  validationErrors: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
  formIdToAutoSubmit: PropTypes.string,
  hideError: PropTypes.bool,
  isMulti: PropTypes.bool,
  clearAfterSelect: PropTypes.bool,
  defaultOptions: PropTypes.bool,
  queryParam: PropTypes.string,
  omitEmptyQuery: PropTypes.bool,
  alerter: PropTypes.shape({
    error: PropTypes.func.isRequired,
  }).isRequired,
}

export default withAlerter(AsyncAutocompleteField)
